import { QueryClient } from '@tanstack/react-query';
import { onAuthStateChanged } from 'firebase/auth';
import { getFirebaseAuth } from '../utils/firebase/firebase';

/**
 * Global error handler for API errors
 * Handles common error scenarios like authentication failures, network errors, etc.
 */
const handleGlobalError = (error: any, context: 'query' | 'mutation' = 'query') => {
    console.error(`Global ${context} error:`, error);

    // Extract error information
    const status = error?.status || error?.response?.status;
    const message = error?.response?.data?.data?.context?.message || error?.response?.data?.message || error?.message || 'Unknown error occurred';
    const streamId = error?.response?.headers?.['x-stream-id'];

    // Log stream ID for debugging if available
    if (streamId) {
        console.error(`Error stream ID: ${streamId}`);
    }

    // Handle specific error cases
    switch (status) {
        case 401:
            console.error('Authentication error - user may need to sign in again');
            break;

        case 403:
            console.error('Permission denied - user lacks required permissions');
            break;

        case 404:
            console.error('Resource not found');
            break;

        case 500:
            console.error('Internal server error');
            break;

        case 502:
        case 503:
        case 504:
            console.error('Service unavailable - server may be down');
            break;

        default:
            if (!status) {
                console.error('Network error - check internet connection');
            } else {
                console.error(`HTTP ${status} error: ${message}`);
            }
    }
};

/**
 * Create a QueryClient with authentication-aware defaults
 */
export const createQueryClient = () => {
    const queryClient = new QueryClient({
        defaultOptions: {
            queries: {
                refetchInterval: false,
                refetchOnWindowFocus: false,
                retry: (failureCount, error: any) => {
                    if (error?.status === 401 || error?.status === 404 || error?.status >= 500) {
                        return false;
                    }
                    return failureCount < 2;
                },
            },
            mutations: {
                retry: false,
                onError: (error: any) => {
                    handleGlobalError(error, 'mutation');
                },
            },
        },
    });

    const setupAuthListener = () => {
        try {
            const auth = getFirebaseAuth();
            onAuthStateChanged(auth, user => {
                if (user) {
                    console.info('User signed in, invalidating auth-dependent queries');
                } else {
                    console.info('User signed out, invalidating auth-dependent queries');
                    void queryClient.invalidateQueries();
                }
            });
        } catch (_error) {
            console.warn('Firebase not initialized yet, auth listener will be set up later');
        }
    };

    setupAuthListener();

    return queryClient;
};

export const queryClient = createQueryClient();

export const withGlobalErrorHandling = () => ({
    onError: (error: unknown) => {
        handleGlobalError(error, 'query');
    },
});

export const withGlobalMutationErrorHandling = () => ({
    onError: (error: unknown) => {
        handleGlobalError(error, 'mutation');
    },
});
